// Interview runtime: pure evaluation of questionnaire visibility, ordering,
// and completeness. Shared by the manual-entry surface and (server-side) the
// call engine so skip behavior can never diverge between the two surfaces.

import {
  MEDICATION_SYSTEM_CATEGORIES_KEY,
  deriveMedicationSystemCategoriesFromAnswers,
} from "./medication-system-categories.js";
import type {
  TenantAdminOutboundQuestionBranchConditionV1,
  TenantAdminOutboundQuestionnaireQuestionV1,
} from "./tenant-admin-outbound-calls-contract.js";

export interface QuestionnairePatientContextV1 {
  ageYears?: number | null;
  sexAtBirth?: string | null;
}

export interface QuestionnaireEvaluationV1 {
  /** Visible questions in sequence order. */
  visibleQuestionKeys: string[];
  /** First visible question without an answer; null when all are answered. */
  nextQuestionKey: string | null;
  /** Visible + required + unanswered. */
  requiredMissingKeys: string[];
  /** Answered keys whose question is currently hidden (e.g. after a go-back
   *  gate flip). Retained by callers, excluded from completeness and from
   *  downstream record writes until the gate re-opens. */
  orphanedAnswerKeys: string[];
  answeredVisibleCount: number;
  visibleCount: number;
}

type EvaluatableQuestion = Pick<
  TenantAdminOutboundQuestionnaireQuestionV1,
  "questionKey" | "sequenceNumber" | "required"
> & {
  skipLogic?: {
    onlyIf?: TenantAdminOutboundQuestionBranchConditionV1;
  } | null;
};

/** Single source of truth for "has this question been answered" — the
 *  evaluator, the entry surface's section stats, and the upsert builder must
 *  agree, or sections show green for answers that were never written. */
export function answerIsPresent(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "";
}

function conditionHolds(
  condition: TenantAdminOutboundQuestionBranchConditionV1,
  effectiveAnswers: Record<string, unknown>,
  context: QuestionnairePatientContextV1,
): boolean {
  // OR-composition, AND-ed with every other constraint below: an umbrella
  // section opens when the patient said yes OR a medication implies it.
  if (condition.anyOf !== undefined && condition.anyOf.length > 0) {
    const anyHolds = condition.anyOf.some((sub) =>
      conditionHolds(sub, effectiveAnswers, context),
    );
    if (!anyHolds) {
      return false;
    }
  }

  const answer = condition.questionKey
    ? effectiveAnswers[condition.questionKey]
    : undefined;

  if (condition.equals !== undefined && answer !== condition.equals) {
    return false;
  }

  if (condition.oneOf !== undefined) {
    const values = Array.isArray(answer) ? answer : [answer];
    if (!values.some((value) => condition.oneOf!.includes(value as never))) {
      return false;
    }
  }

  const containsAnyValues = condition.containsAny ?? condition.valuesAnyOf;
  if (containsAnyValues !== undefined) {
    const values = Array.isArray(answer)
      ? answer.map(String)
      : [String(answer ?? "")];
    if (!values.some((value) => containsAnyValues.includes(value))) {
      return false;
    }
  }

  if (condition.answerStatus === "answered" && !answerIsPresent(answer)) {
    return false;
  }
  if (condition.answerStatus === "skipped" && answerIsPresent(answer)) {
    return false;
  }

  const age = context.ageYears;
  if (condition.patientAgeYearsAtLeast !== undefined) {
    if (age == null || age < condition.patientAgeYearsAtLeast) return false;
  }
  if (condition.patientAgeYearsAtMost !== undefined) {
    if (age == null || age > condition.patientAgeYearsAtMost) return false;
  }

  const sex = context.sexAtBirth;
  if (condition.patientSexAtBirthOneOf !== undefined) {
    if (sex == null || !condition.patientSexAtBirthOneOf.includes(sex)) {
      return false;
    }
  }
  if (condition.patientSexAtBirthNotOneOf !== undefined) {
    if (sex != null && condition.patientSexAtBirthNotOneOf.includes(sex)) {
      return false;
    }
  }

  return true;
}

export function evaluateQuestionnaireV1(
  questions: EvaluatableQuestion[],
  answers: Record<string, unknown>,
  context: QuestionnairePatientContextV1,
): QuestionnaireEvaluationV1 {
  const ordered = [...questions].sort(
    (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0),
  );

  // Walk in sequence order so a hidden gate's answer can never activate its
  // children: only answers of *visible* questions feed later conditions.
  const effectiveAnswers: Record<string, unknown> = {};

  // Derived medication categories are seeded up-front so umbrella sections
  // can gate on them regardless of question order. Derived from ALL answer
  // text (minus allergy questions) — every surface that evaluates
  // visibility computes the SAME categories from the same answers, so voice,
  // chart ingest and manual entry can never disagree about which sections a
  // medication opened. A real answer under the same key would win below.
  const derivedCategories = deriveMedicationSystemCategoriesFromAnswers(answers);
  if (derivedCategories.length > 0) {
    effectiveAnswers[MEDICATION_SYSTEM_CATEGORIES_KEY] = derivedCategories;
  }
  const visibleQuestionKeys: string[] = [];
  const requiredMissingKeys: string[] = [];
  let nextQuestionKey: string | null = null;
  let answeredVisibleCount = 0;

  for (const question of ordered) {
    const onlyIf = question.skipLogic?.onlyIf;
    const visible =
      !onlyIf || conditionHolds(onlyIf, effectiveAnswers, context);

    if (!visible) continue;

    visibleQuestionKeys.push(question.questionKey);
    const answered = answerIsPresent(answers[question.questionKey]);

    if (answered) {
      effectiveAnswers[question.questionKey] = answers[question.questionKey];
      answeredVisibleCount += 1;
    } else {
      if (nextQuestionKey == null) nextQuestionKey = question.questionKey;
      if (question.required) requiredMissingKeys.push(question.questionKey);
    }
  }

  const visibleSet = new Set(visibleQuestionKeys);
  const orphanedAnswerKeys = Object.keys(answers).filter(
    (key) => answerIsPresent(answers[key]) && !visibleSet.has(key),
  );

  return {
    visibleQuestionKeys,
    nextQuestionKey,
    requiredMissingKeys,
    orphanedAnswerKeys,
    answeredVisibleCount,
    visibleCount: visibleQuestionKeys.length,
  };
}
